import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ENDPOINT from '../../constants/endpoints';
import Button from '../../GbsUI/UI/Button';
import Radio from '../../GbsUI/UI/Radio';
import TextInput from '../../GbsUI/UI/TextInput';
import useApi from '../../hooks/useApi';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import Popup from '../Popup';
import './styles.css';

const EmailPreferencesPopup = ({ isOpen, onClose, candidateId }) => {
  const api = useApi();
  const [mode, setMode] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const minDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);

    return `${date.getFullYear()}-${month}-${day}`;
  }, []);

  const getDefaultEndDate = useCallback(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 6);
    return date;
  }, []);

  const getData = useCallback(() => {
    api.get(
      ENDPOINT.ENTITY.CANDIDATE_PAUSE_EMAILS,
      {
        candidateId,
      },
      {
        handleError: () => {},
        handleSuccess: response => {
          setMode(response.mode);
          setEndDate(response.endDate);
        },
      }
    );
  }, [api, candidateId]);

  const onModeChange = useCallback((value) => {
    setMode(value);

    if (value === 'OffMarket' || value === 'Soft') {
      const date = getDefaultEndDate();
      const month = `0${date.getMonth() + 1}`.slice(-2);
      const day = `0${date.getDate()}`.slice(-2);
      setEndDate(`${date.getFullYear()}-${month}-${day}`);
    } else {
      setEndDate('');
    }
  }, [getDefaultEndDate]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    setSubmitting(true);
    const data = {
      candidateId,
      mode,
      endDate,
    };

    api.post(
      ENDPOINT.ENTITY.CANDIDATE_PAUSE_EMAILS,
      data,
      {
        handleError: response => {
          ResponseErrorDialog({ response });
          setSubmitting(false);
        },
        handleSuccess: () => {
          setSubmitting(false);
          toast.success('Email preferences updated successfully.');
          onClose();
        },
      }
    );

  }, [api, candidateId, endDate, mode, onClose]);

  useEffect(() => {
    if (isOpen) {
      getData();
    }

    if (!isOpen) {
      setMode('');
      setEndDate('');
    }
  }, [getData, isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <Popup
      actions={{
        dismiss: onClose,
      }}
      bodyRender={(
        <form onSubmit={handleSubmit} className={'email-preferences-popup'}>
          <Radio
            required
            name={'state'}
            options={[
              {
                label: 'No',
                value: 'No',
                help: 'Set the email preferences to default.'
              },
              {
                label: 'Hard',
                value: 'Hard',
                help: 'stops all the emails, except for some system emails, like forgot password email.'
              },
              {
                label: 'Soft',
                value: 'Soft',
                help: 'pauses all the emails (except for newsletter) for 6 months.'
              },
              {
                label: 'OffMarket',
                value: 'OffMarket',
                help: 'also pauses all the emails (except for the newsletter), but you can select there any custom period.'
              },
            ]}
            checked={mode}
            onChange={e => {
              onModeChange(e.target.value);
            }}
          />

          {(mode === 'OffMarket' || mode === 'Soft') && (
            <div className={'email-preferences-popup__sub-input'}>
              <TextInput
                required
                label={'Pause period'}
                name={'pausePeriod'}
                type={'date'}
                min={minDate}
                value={endDate}
                onChange={e => {
                  setEndDate(e.target.value);
                }}
                lang="en"
              />
              <div className={'email-preferences-popup__sub-actions'}>
                <span
                  onClick={() => {
                    const date = getDefaultEndDate();
                    const month = `0${date.getMonth() + 1}`.slice(-2);
                    const day = `0${date.getDate()}`.slice(-2);

                    setEndDate(`${date.getFullYear()}-${month}-${day}`);
                  }}
                >
                  +6 Month
                </span>
                <span
                  onClick={() => {
                    const date = getDefaultEndDate();
                    date.setMonth(date.getMonth() + 12);
                    const month = `0${date.getMonth() + 1}`.slice(-2);
                    const day = `0${date.getDate()}`.slice(-2);

                    setEndDate(`${date.getFullYear()}-${month}-${day}`);
                  }}
                >
                  +18 Month
                </span>
              </div>
            </div>
          )}

          <Button
            className={'email-preferences-popup__submit'}
            size={'l'}
            iconProps={{ size: 21 }}
            iconPosition={'right'}
            disabled={submitting}
          >
            Apply
          </Button>
        </form>
      )}
      modeless={true}
      popupStyles={{
        maxWidth: '550px',
        padding: '40px 30px 10px',
      }}
      hasCloseButton={true}
    />
  )
};

export default EmailPreferencesPopup;
